import React, { useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  ClientResponse,
  TerminalResponse,
  CertificateResponse,
} from '../../../types'
import { useSnackbar } from 'notistack'

interface DataSigningProps {
  selectedClient: ClientResponse | null
  selectedCertificate: {
    cert: CertificateResponse
    terminal: TerminalResponse
  } | null
  remoteSign: (
    client: ClientResponse,
    certificate: { cert: CertificateResponse; terminal: TerminalResponse },
    dataToSign: string,
    pinCode: string,
  ) => Promise<any>
  pinCode: string
}

export const DataSigning: React.FC<DataSigningProps> = ({
  selectedClient,
  selectedCertificate,
  remoteSign,
  pinCode,
}) => {
  const [dataToSign, setDataToSign] = useState<string>('Hello from client!')
  const [signatureResult, setSignatureResult] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const handleSignData = async () => {
    if (!selectedClient) {
      enqueueSnackbar('Lütfen bir client seçin!', { variant: 'warning' })
      return
    }

    if (!selectedCertificate) {
      enqueueSnackbar('Lütfen bir sertifika seçin!', { variant: 'warning' })
      return
    }

    if (!pinCode.trim()) {
      enqueueSnackbar('Lütfen PIN kodunu girin!', { variant: 'warning' })
      return
    }

    if (!dataToSign.trim()) {
      enqueueSnackbar('Lütfen imzalanacak veriyi girin!', { variant: 'warning' })
      return
    }

    setLoading(true)
    try {
      const result = await remoteSign(
        selectedClient,
        selectedCertificate,
        dataToSign,
        pinCode,
      )

      setSignatureResult(result.signature || result)
    } catch (error) {
      console.error('Failed to sign data:', error)
      enqueueSnackbar('İmzalama işlemi başarısız: ' + error, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const canSign =
    selectedClient && selectedCertificate && pinCode.trim() && dataToSign.trim()

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Veri İmzalama
        </Typography>

        {!selectedClient || !selectedCertificate ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Veri imzalamak için önce bir client ve sertifika seçin.
          </Alert>
        ) : (
          <Box>
            <TextField
              fullWidth
              label="İmzalanacak Veri"
              multiline
              rows={4}
              value={dataToSign}
              onChange={(e) => setDataToSign(e.target.value)}
              placeholder="İmzalanacak veriyi girin"
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              onClick={handleSignData}
              disabled={!canSign || loading}
              fullWidth
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Veriyi İmzala'}
            </Button>
          </Box>
        )}

        {signatureResult && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              İmzalama işlemi tamamlandı!
            </Alert>
            <TextField
              fullWidth
              label="İmza Sonucu (Base64)"
              multiline
              rows={6}
              value={
                typeof signatureResult === 'string'
                  ? signatureResult
                  : JSON.stringify(signatureResult, null, 2)
              }
              InputProps={{ readOnly: true }}
              sx={{ fontFamily: 'monospace', fontSize: '12px' }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
